import { Loading, ModalProvider } from '@shape-ds/components';
import { useFeatureFlag, FeatureFlagButton } from '@shape/feature-flag';
import { Layout } from 'components/Layout/Layout';
import { Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';

import { ASSET_PATH, EVENT_PATH, HOME_PATH, MODEL_PATH } from 'config/paths';
import { PrivateRoute } from './PrivateRoute';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'components/ErrorFallback/ErrorFallback';
import { PDFSelector } from 'pages/Pdf/PDFPreview';
import ChatWidget from 'components/ChatWidget/ChatWidget';

const NotFound = lazy(() => import('pages/NotFound/NotFound'));
const Login = lazy(() => import('pages/Login/Login'));
const Asset = lazy(() => import('pages/Asset/Asset'));
const Event = lazy(() => import('pages/Event/Event'));
const Flare = lazy(() => import('@shape/flare'));

// Asset page tabs
const Overview = lazy(() => import('pages/Asset/tabs/Overview/Overview'));
const Model = lazy(() => import('pages/Model/Model'));
const EventsPage = lazy(() => import('pages/Asset/tabs/Events/Events.page'));

const Monitoring = lazy(() => import('pages/Asset/tabs/Monitoring/monitoring'));
const Cases = lazy(() => import('pages/Asset/tabs/Cases/ExpandedCase/ExpandedCase'));
const CasesPage = lazy(() => import('pages/Asset/tabs/Cases/Cases.page'));
const OilSystem = lazy(() => import('pages/Reports/Performance'));
const Rert = lazy(() => import('pages/Reports/Rert/Rert'));

export const AppRoutes = () => {
  const flagOilSystem = useFeatureFlag('oil-system');
  const flagFlare = useFeatureFlag('flare-modec');
  const isNewPdf = useFeatureFlag('new-pdf-rert');

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path={HOME_PATH}
        element={
          <PrivateRoute>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <ModalProvider>
                <Outlet />
                <FeatureFlagButton />
                <ChatWidget />
              </ModalProvider>
            </ErrorBoundary>
          </PrivateRoute>
        }
      >
        <Route path={HOME_PATH} element={<Navigate to={`/${ASSET_PATH}/overview`} />} />
        <Route path="cases" element={<Layout />}>
          <Route
            index
            path={':status?'}
            element={
              <Suspense fallback={<Loading center />}>
                <CasesPage />
              </Suspense>
            }
          />

          <Route path="create" element={<Cases />} />
          <Route path="edit/:caseId" element={<Cases />} />
        </Route>
        <Route path={ASSET_PATH} element={<Layout />}>
          <Route
            path=":assetId?"
            element={
              <PrivateRoute>
                <Suspense fallback={<Loading center />}>
                  <Asset />
                </Suspense>
              </PrivateRoute>
            }
          >
            <Route index element={<Overview />} />
            <Route path="overview" element={<Overview />} />
            <Route path="monitoring" element={<Monitoring />} />
            <Route path="health" element={<Rert />} />
            {/* // deprecated route kept for backward compatibility */}
            <Route path="cases" element={<Navigate to={`/cases`} />}></Route>
            <Route path="events" element={<Navigate to={`/events`} />}></Route>
            <Route path="asset_health" element={<Navigate to={`/health`} />} />
          </Route>
        </Route>
        <Route path="oil_system" element={<Layout />}>
          <Route
            index
            element={
              flagOilSystem ? (
                <Suspense fallback={<Loading center />}>
                  <OilSystem />
                </Suspense>
              ) : (
                <Navigate to={HOME_PATH} />
              )
            }
          />
        </Route>
        <Route path="flare" element={<Layout />}>
          {flagFlare && (
            <Route
              index
              element={
                <Suspense fallback={<Loading center />}>
                  <Flare />
                </Suspense>
              }
            />
          )}
        </Route>
        <Route path="events/:status?" element={<Layout />}>
          <Route
            index
            element={
              <Suspense fallback={<Loading />}>
                <EventsPage />
              </Suspense>
            }
          />
        </Route>
        <Route path={MODEL_PATH} element={<Layout />}>
          <Route
            index
            element={
              <PrivateRoute>
                <Model />
              </PrivateRoute>
            }
          />
          <Route
            path=":assetId/:modelName"
            element={
              <PrivateRoute>
                <Model />
              </PrivateRoute>
            }
          />
        </Route>
        <Route path={EVENT_PATH} element={<Layout />}>
          <Route
            index
            element={
              <PrivateRoute>
                <Navigate to={HOME_PATH} replace />
              </PrivateRoute>
            }
          />
          <Route
            path=":eventId"
            element={
              <PrivateRoute>
                <Suspense fallback={<Loading />}>
                  <Event />
                </Suspense>
              </PrivateRoute>
            }
          />
        </Route>

        <Route
          path="/login"
          element={
            <Suspense fallback={<Loading />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/redirectB2c"
          element={
            <Suspense fallback={<Loading />}>
              <NotFound />
            </Suspense>
          }
        />
        {isNewPdf && (
          <Route path="pdf" element={<Layout />}>
            <Route
              index
              path=":assetParam?"
              element={
                <PrivateRoute>
                  <PDFSelector />
                </PrivateRoute>
              }
            />
          </Route>
        )}
        <Route
          path="*"
          element={
            <Suspense fallback={<Loading />}>
              <NotFound />
            </Suspense>
          }
        />
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
};
